import { Link } from "@tanstack/react-router";
import { Gift, LifeBuoy, ListChecks, ShieldCheck, Users, Wallet } from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

const FAQ_EN: [string, string][] = [
  ["How much do I earn?", "2 Heat when someone signs up through your link, then 10 Heat every time that person spends US$100 on BeHot.Now — boosts, listings, marketplace orders or featuring all count."],
  ["When is the Heat credited?", "Signup Heat lands once the account is confirmed. Spending Heat is credited automatically after the payment is settled, usually within minutes."],
  ["Where do I find my link?", "In Referrals inside your dashboard, and on your dashboard overview. It is your public profile link plus your personal ?ref= code."],
  ["Can I withdraw referral Heat?", "No. Referral Heat is internal credit: it pays for boosts on any listing and for Marketplace orders and featuring. Withdrawable money only comes from the Earn wallet."],
  ["Does the referral expire?", "No. Once someone is attributed to you, their qualifying spending keeps generating Heat for you."],
  ["What if two people share a link with the same person?", "The first code stored on that visitor's device wins, and it is locked to the account at signup."],
  ["Can businesses or agencies refer?", "Yes. Any account — personal, professional or business — can use the referral programme under the same rules."],
  ["What breaks the rules?", "Creating accounts yourself, incentivised fake signups, spam and misleading claims about the platform. These reverse the Heat and can close the account."],
];

const STEP_KEYS = ["step1", "step2", "step3", "step4", "step5"] as const;
const RULE_KEYS = ["rule1", "rule2", "rule3", "rule4", "rule5", "rule6"] as const;
const TRUST_KEYS = [
  { key: "trust1", icon: ShieldCheck },
  { key: "trust2", icon: Wallet },
  { key: "trust3", icon: Users },
];
const FAQ_KEYS = FAQ_EN.map((_, i) => `faq${i + 1}`);

export function ReferralsHowItWorks() {
  const { t } = useT();

  const STEPS = STEP_KEYS.map((k) => ({
    key: k,
    title: t(`hiw.referrals.${k}.title`),
    body: t(`hiw.referrals.${k}.body`),
  }));
  const RULES = RULE_KEYS.map((k) => t(`hiw.referrals.${k}`));
  const TRUST = TRUST_KEYS.map((p) => ({
    ...p,
    title: t(`hiw.referrals.${p.key}.title`),
    body: t(`hiw.referrals.${p.key}.body`),
  }));
  const FAQ: [string, string][] = FAQ_KEYS.map((k) => [t(`hiw.referrals.${k}.q`), t(`hiw.referrals.${k}.a`)]);

  return (
    <div>
      <p className="mt-8 max-w-2xl text-muted-foreground">{t("hiw.referrals.intro")}</p>

      <section className="mt-12" aria-labelledby="ref-steps-heading">
        <div className="flex items-center gap-2">
          <Gift className="size-5 text-primary" aria-hidden="true" />
          <h2 id="ref-steps-heading" className="font-display text-2xl font-bold">
            {t("hiw.referrals.stepsHeading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {STEPS.map((s) => (
              <li key={s.key} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("hiw.referrals.startTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("hiw.referrals.startBody")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/app/referrals">{t("hiw.referrals.openReferrals")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/referrals">{t("hiw.referrals.programmePage")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="ref-rates-heading">
        <h2 id="ref-rates-heading" className="font-display text-2xl font-bold">
          {t("hiw.referrals.ratesHeading")}
        </h2>
        <div className="card-surface mt-5 grid gap-4 p-5 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.referrals.ratePerSignupLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold text-primary">2 Heat</p>
            <p className="text-xs text-muted-foreground">{t("hiw.referrals.ratePerSignupDesc")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.referrals.ratePerSpendLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold">10 Heat</p>
            <p className="text-xs text-muted-foreground">{t("hiw.referrals.ratePerSpendDesc")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.referrals.rateUsableLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold">{t("hiw.referrals.rateUsableValue")}</p>
            <p className="text-xs text-muted-foreground">{t("hiw.referrals.rateUsableDesc")}</p>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="ref-rules-heading">
        <h2 id="ref-rules-heading" className="font-display text-2xl font-bold">
          {t("hiw.referrals.rulesHeading")}
        </h2>
        <div className="card-surface mt-5 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="ref-trust-heading">
        <h2 id="ref-trust-heading" className="font-display text-2xl font-bold">
          {t("hiw.referrals.trustHeading")}
        </h2>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-3">
          {TRUST.map((p) => (
            <li key={p.key} className="card-surface p-5">
              <p.icon className="size-5 text-primary" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="ref-faq-heading">
        <div className="flex flex-wrap items-center gap-2">
          <ListChecks className="size-5 text-primary" aria-hidden="true" />
          <h2 id="ref-faq-heading" className="font-display text-2xl font-bold">
            {t("hiw.referrals.faqHeading")}
          </h2>
          <Badge variant="outline">{t("hiw.referrals.faqCount", { count: FAQ.length })}</Badge>
        </div>
        <div className="mt-5 divide-y divide-border rounded-xl border border-border">
          {FAQ.map(([q, a], i) => (
            <details key={q} open={i < 3} className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
              <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="ref-contact-heading">
        <div className="flex items-center gap-2">
          <LifeBuoy className="size-5 text-primary" aria-hidden="true" />
          <h2 id="ref-contact-heading" className="font-display text-2xl font-bold">
            {t("hiw.referrals.contactHeading")}
          </h2>
        </div>
        <div className="mt-5">
          <ContactForm defaultTopic="Referral programme" />
        </div>
      </section>
    </div>
  );
}

export const REFERRAL_FAQ = FAQ_EN;
